import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface SessionState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  userEmail: string | null;
  userName: string | null;
  userId: string | null;
  roles: string[];
  roleNames: string[];
  permissions: string[];
  tenantId: string | null;
  tenantIds: string[];
  subscriptionStatus: string;
  userOnboardingCompleted: boolean;
  /**
   * `tenant_memberships.sam_welcome_shown` — flipped by the backend on the user's first-ever
   * SAM chat. Once true, every onboarding redirect (useOnboardingRoute, UserOnboardingPage)
   * short-circuits to /chat so a returning member never gets bounced back through the wizard
   * for a lingering per-user checklist step.
   */
  samWelcomeShown: boolean;
  /**
   * One-shot signal: set by SetupWizardPage right before navigating to /chat when the user
   * finished onboarding while an onboarding voice ("Talk to SAM") session was active — carries
   * "was already in voice mode" across the client-side navigate so ChatPage can auto-start
   * interactive mode instead of landing in text mode. Deliberately absent from
   * sessionPersistConfig.whitelist in store.ts, so it only survives the in-app SPA navigation,
   * never a page reload — ChatPage also clears it immediately after consuming it once.
   */
  pendingVoiceHandoff: boolean;
}

const initialState: SessionState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  userEmail: null,
  userName: null,
  userId: null,
  roles: [],
  roleNames: [],
  permissions: [],
  tenantId: null,
  tenantIds: [],
  subscriptionStatus: 'not_selected',
  userOnboardingCompleted: false,
  samWelcomeShown: false,
  pendingVoiceHandoff: false,
};

const sessionStateSlice = createSlice({
  name: 'sessionState',
  initialState,
  reducers: {
    setSession(
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
        user: {
          user_id: string;
          email: string;
          name: string;
          roles: string[];
          role_names?: string[];
          permissions?: string[];
          tenant_id: string;
          subscription_status?: string;
          onboarding_completed?: boolean;
          sam_welcome_shown?: boolean;
        };
      }>
    ) {
      const { accessToken, refreshToken, user } = action.payload;
      state.isAuthenticated = true;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.userEmail = user.email;
      state.userName = user.name;
      state.userId = user.user_id;
      state.roles = user.roles;
      state.roleNames = user.role_names ?? [];
      state.permissions = user.permissions ?? [];
      state.tenantId = user.tenant_id;
      state.subscriptionStatus = user.subscription_status ?? 'not_selected';
      state.userOnboardingCompleted = user.onboarding_completed ?? false;
      state.samWelcomeShown = user.sam_welcome_shown ?? false;
    },
    clearSession(state) {
      Object.assign(state, initialState);
    },
    refreshSession(
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken?: string;
        roles?: string[];
        permissions?: string[];
        subscription_status?: string;
      }>
    ) {
      const { accessToken, refreshToken, roles, permissions, subscription_status } = action.payload;
      state.accessToken = accessToken;
      // The backend rotates the refresh token on every refresh (single-use).
      // Persist the new one or the next refresh will present a revoked token.
      if (refreshToken) {
        state.refreshToken = refreshToken;
      }
      if (roles) {
        state.roles = roles;
      }
      if (permissions) {
        state.permissions = permissions;
      }
      if (subscription_status) {
        state.subscriptionStatus = subscription_status;
      }
    },
    setSubscriptionStatus(state, action: PayloadAction<string>) {
      state.subscriptionStatus = action.payload;
    },
    setUserOnboardingCompleted(state, action: PayloadAction<boolean>) {
      state.userOnboardingCompleted = action.payload;
    },
    setUserName(state, action: PayloadAction<string>) {
      state.userName = action.payload;
    },
    setRoleNames(state, action: PayloadAction<string[]>) {
      state.roleNames = action.payload;
    },
    setPendingVoiceHandoff(state, action: PayloadAction<boolean>) {
      state.pendingVoiceHandoff = action.payload;
    },
  },
});

export const { setSession, clearSession, refreshSession, setSubscriptionStatus, setUserOnboardingCompleted, setUserName, setRoleNames, setPendingVoiceHandoff } =
  sessionStateSlice.actions;
export default sessionStateSlice.reducer;
